import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
ChartJS.register(ArcElement, Tooltip, Legend);

function MatchHistory() {
  const [nickname, setNickname] = useState('');
  const [player, setPlayer] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPlayer(null);
    setMatches([]);

    try {
      const res = await fetch(`https://faceit-api-lsyl.onrender.com/api/player/matches?nickname=${nickname}`);
      const data = await res.json();

      if (data.error) throw new Error(data.error);

      setPlayer(data.player);
      setMatches(data.matches);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredMatches = matches.filter((match) => {
    if (!selectedMonth) return true;
    const matchDate = new Date(match.started_at * 1000);
    const matchMonth = `${matchDate.getFullYear()}-${String(matchDate.getMonth() + 1).padStart(2, '0')}`;
    return matchMonth === selectedMonth;
  });

  return (
    <div className="min-h-screen w-full bg-base-200 text-base-content py-10">
      <div className="w-full max-w-4xl px-4 text-center mx-auto flex flex-col items-center">
        <h1 className="text-4xl font-bold mb-6">Warface Match History Lookup</h1>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8">
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Enter FACEIT nickname"
            className="input input-bordered w-full max-w-xs text-center"
          />
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </form>

        {loading && (
          <div className="flex flex-col items-center justify-center my-8">
            <span className="loading loading-spinner loading-lg text-primary mb-2"></span>
            <p className="text-info text-lg">Loading match data (last 1000 matches)...</p>
          </div>
        )}
        {error && <p className="text-error">{error}</p>}

        {matches.length > 0 && (
          <div className="mb-6 w-full max-w-xs">
            <select
              className="select select-bordered w-full"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">All Months</option>
              {Array.from({ length: 12 }, (_, i) => {
                const month = new Date(2025, i).toLocaleString('default', { month: 'long' });
                return (
                  <option key={i} value={`${2025}-${String(i + 1).padStart(2, '0')}`}>
                    {month} 2025
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {filteredMatches.length > 0 && (
          <div className="flex flex-col lg:flex-row justify-center items-start gap-6 mb-6 w-full">
            <div className="card bg-base-100 shadow p-4 w-full max-w-xs">
              <h3 className="text-lg font-semibold mb-4">Win/Loss Breakdown</h3>
              <div className="flex justify-center" style={{ height: 150 }}>
                <Pie
                  data={{
                    labels: ['Wins', 'Losses'],
                    datasets: [{
                      data: filteredMatches.reduce(
                        (acc, match) => {
                          const winner = match.details?.results?.winner;
                          const faction1Players = match.details?.teams?.faction1?.roster || [];
                          const faction2Players = match.details?.teams?.faction2?.roster || [];
                          const isWin =
                            (winner === 'faction1' && faction1Players.some(p => p.player_id === player?.player_id)) ||
                            (winner === 'faction2' && faction2Players.some(p => p.player_id === player?.player_id));
                          if (isWin) acc[0] += 1;
                          else acc[1] += 1;
                          return acc;
                        },
                        [0, 0]
                      ),
                      backgroundColor: ['#36d399', '#f87272'],
                    }]
                  }}
                  options={{ responsive: true, plugins: { legend: { display: false } } }}
                />
              </div>
            </div>

            <div className="card bg-base-100 shadow p-4 w-full max-w-xs">
              <h3 className="text-lg font-semibold mb-4">Competition Breakdown</h3>
              <div className="flex justify-center" style={{ height: 150 }}>
                <Pie
                  data={{
                    labels: Object.keys(filteredMatches.reduce((acc, match) => {
                      const name = match.competition_name || 'Unknown';
                      acc[name] = (acc[name] || 0) + 1;
                      return acc;
                    }, {})),
                    datasets: [{
                      data: Object.values(filteredMatches.reduce((acc, match) => {
                        const name = match.competition_name || 'Unknown';
                        acc[name] = (acc[name] || 0) + 1;
                        return acc;
                      }, {})),
                      backgroundColor: ['#3abff8', '#f472b6', '#facc15', '#34d399', '#60a5fa', '#f87171'],
                    }]
                  }}
                  options={{ responsive: true, plugins: { legend: { display: false } } }}
                />
              </div>
            </div>
          </div>
        )}

        {player && (
          <div className="card bg-base-100 shadow p-6 mb-6 w-full">
            <h2 className="text-xl font-bold">Player: {player.nickname}</h2>
            <p><strong>Player ID:</strong> {player.player_id}</p>
          </div>
        )}

        {filteredMatches.length > 0 && (
          <div className="card bg-base-100 shadow p-6 w-full">
            <h3 className="text-xl font-semibold mb-4">Recent Warface Matches</h3>
            <div className="w-full">
              <table className="table table-zebra w-full text-center text-sm">
                <thead className="text-primary">
                  <tr>
                    <th className="text-center">Date</th>
                    <th className="text-center">Match Link</th>
                    <th className="text-center">Competition</th>
                    <th className="text-center">Faction 1</th>
                    <th className="text-center">Faction 2</th>
                    <th className="text-center">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMatches.map((match) => {
                    const { match_id, started_at, details } = match;

                    const date = new Date(started_at * 1000).toLocaleString();
                    const matchLink = `https://www.faceit.com/en/warface/room/${match_id}`;
                    const winner = details?.results?.winner === 'faction1'
                      ? 'Faction 1'
                      : details?.results?.winner === 'faction2'
                      ? 'Faction 2'
                      : 'Unknown';

                    const faction1Players = details?.teams?.faction1?.roster || [];
                    const faction2Players = details?.teams?.faction2?.roster || [];

                    return (
                      <tr key={match_id}>
                        <td className="text-center whitespace-normal">{date}</td>
                        <td className="text-center whitespace-normal">
                          <a href={matchLink} target="_blank" rel="noreferrer" className="link link-primary">
                            View Match
                          </a>
                        </td>
                        <td className="text-center whitespace-normal">{match.competition_name || <i>Unknown</i>}</td>
                        <td className="text-center whitespace-normal">
                          {faction1Players.length > 0 ? (
                            faction1Players.map((p) => (
                              <div key={p.player_id}>
                                <a
                                  href={`https://www.faceit.com/en/players/${p.nickname}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="link link-info"
                                >
                                  {p.nickname}
                                </a>
                              </div>
                            ))
                          ) : (
                            <i>No data</i>
                          )}
                        </td>
                        <td className="text-center whitespace-normal">
                          {faction2Players.length > 0 ? (
                            faction2Players.map((p) => (
                              <div key={p.player_id}>
                                <a
                                  href={`https://www.faceit.com/en/players/${p.nickname}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="link link-info"
                                >
                                  {p.nickname}
                                </a>
                              </div>
                            ))
                          ) : (
                            <i>No data</i>
                          )}
                        </td>
                        <td className="text-center whitespace-normal">
                          {(() => {
                            if (winner === 'Faction 1' && faction1Players.some(p => p.player_id === player?.player_id)) {
                              return 'Win';
                            }
                            if (winner === 'Faction 2' && faction2Players.some(p => p.player_id === player?.player_id)) {
                              return 'Win';
                            }
                            return 'Loss';
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MatchHistory;